/* eslint-disable no-console */
/**
 * Production-Safe Logger Service
 *
 * Replaces console.log with structured, environment-aware logging
 * Features:
 * - Environment-aware (auto-disabled in production)
 * - Structured logging with context
 * - Performance tracking
 * - Error tracking integration ready
 * - Scoped loggers for better organization
 * - Privacy-safe (no sensitive data in production)
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'performance';

interface LogContext {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any -- LogContext must accept Error objects and arbitrary log metadata
  userId?: string;
  petId?: string;
  docId?: string;
  filename?: string;
  error?: Error | unknown;
}

interface PerformanceMetric {
  operation: string;
  durationMs: number;
  context?: LogContext;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isProduction = process.env.NODE_ENV === 'production';
  private isTest = process.env.NODE_ENV === 'test';
  
  // Performance metrics storage (for analytics)
  private performanceMetrics: PerformanceMetric[] = [];
  
  // Configuration
  private config = {
    enableDebug: !this.isProduction,
    enableInfo: !this.isProduction,
    enableWarn: true,
    enableError: true,
    enablePerformance: !this.isProduction,
    maxMetricsStored: 100,
  };

  /**
   * Debug - Detailed diagnostic information
   * Only shows in development, never in production
   * Use for: Tracing code execution, variable values, flow control
   */
  debug(message: string, context?: LogContext): void {
    // Early return for performance - no string operations if disabled
    if (!this.config.enableDebug || this.isTest) return;
    const timestamp = new Date().toISOString();
    console.log(`🔍 [DEBUG ${timestamp}]`, message, context ? this.sanitizeContext(context) : '');
  }

  /**
   * Info - General informational messages
   * Shows in development/staging, disabled in production
   * Use for: Successful operations, status updates, milestones
   */
  info(message: string, context?: LogContext): void {
    // Early return for performance - no string operations if disabled
    if (!this.config.enableInfo || this.isTest) return;
    const timestamp = new Date().toISOString();
    console.log(`ℹ️ [INFO ${timestamp}]`, message, context ? this.sanitizeContext(context) : '');
  }

  /**
   * Warning - Something unexpected but not breaking
   * Always logged and sent to error tracking
   * Use for: Deprecated features, missing optional data, fallback actions
   */
  warn(message: string, context?: LogContext): void {
    if (this.config.enableWarn) {
      const timestamp = new Date().toISOString();
      console.warn(`⚠️ [WARN ${timestamp}]`, message, context ? this.sanitizeContext(context) : '');
      
      // Send to error tracking in production
      if (this.isProduction) {
        this.sendToErrorTracking('warn', message, context);
      }
    }
  }

  /**
   * Error - Something went wrong
   * Always logged and sent to error tracking
   * Use for: Exceptions, failed operations, critical issues
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (this.config.enableError) {
      const timestamp = new Date().toISOString();
      const errorContext = {
        ...context,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        } : error,
      };
      
      console.error(`🚨 [ERROR ${timestamp}]`, message, errorContext);
      
      // Always send errors to tracking (even in development for debugging)
      this.sendToErrorTracking('error', message, errorContext);
    }
  }

  /**
   * Performance - Track operation timing
   * Only shows in development, stores metrics for analytics
   * Use for: Measuring operation duration, identifying bottlenecks
   */
  performance(operation: string, durationMs: number, context?: LogContext): void {
    // Early return for performance - no string operations if disabled
    if (!this.config.enablePerformance) return;
    const timestamp = new Date().toISOString();
    const performanceLevel = durationMs > 1000 ? '🐌 SLOW' : durationMs > 500 ? '⚠️ MODERATE' : '⚡ FAST';
    
    console.log(
      `⏱️ [PERF ${timestamp}] ${performanceLevel}`,
      `${operation}: ${durationMs.toFixed(2)}ms`,
      context ? this.sanitizeContext(context) : ''
    );
    
    // Store metric for analytics
    this.storePerformanceMetric({ operation, durationMs, context });
  }

  /**
   * Success - Highlight successful operations
   * Use for: Major milestones, successful completions
   */
  success(message: string, context?: LogContext): void {
    // Early return for performance - no string operations if disabled
    if (!this.config.enableInfo || this.isTest) return;
    const timestamp = new Date().toISOString();
    console.log(`✅ [SUCCESS ${timestamp}]`, message, context ? this.sanitizeContext(context) : '');
  }

  /**
   * Create a scoped logger with default context
   * Useful for adding consistent context to all logs in a module
   */
  createScope(scope: string, defaultContext?: LogContext) {
    const scopedContext = { scope, ...defaultContext };
    
    return {
      debug: (msg: string, ctx?: LogContext) => 
        this.debug(msg, { ...scopedContext, ...ctx }),
      info: (msg: string, ctx?: LogContext) => 
        this.info(msg, { ...scopedContext, ...ctx }),
      warn: (msg: string, ctx?: LogContext) => 
        this.warn(msg, { ...scopedContext, ...ctx }),
      error: (msg: string, err?: Error | unknown, ctx?: LogContext) =>
        this.error(msg, err, { ...scopedContext, ...ctx }),
      success: (msg: string, ctx?: LogContext) => 
        this.success(msg, { ...scopedContext, ...ctx }),
      performance: (operation: string, durationMs: number, ctx?: LogContext) => 
        this.performance(operation, durationMs, { ...scopedContext, ...ctx }),
      
      // Utility: Time an operation
      time: async <T>(operation: string, fn: () => Promise<T>, ctx?: LogContext): Promise<T> => {
        const startTime = performance.now();
        try {
          const result = await fn();
          const duration = performance.now() - startTime;
          this.performance(operation, duration, { ...scopedContext, ...ctx, success: true });
          return result;
        } catch (error) {
          const duration = performance.now() - startTime;
          this.performance(operation, duration, { ...scopedContext, ...ctx, success: false });
          this.error(`${operation} failed`, error as Error, { ...scopedContext, ...ctx });
          throw error;
        }
      },
    };
  }

  /**
   * Utility: Time a synchronous operation
   */
  timeSync<T>(operation: string, fn: () => T, context?: LogContext): T {
    const startTime = performance.now();
    try {
      const result = fn();
      const duration = performance.now() - startTime;
      this.performance(operation, duration, { ...context, success: true });
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.performance(operation, duration, { ...context, success: false });
      this.error(`${operation} failed`, error as Error, context);
      throw error;
    }
  }

  /**
   * Utility: Time an async operation
   */
  async timeAsync<T>(operation: string, fn: () => Promise<T>, context?: LogContext): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - startTime;
      this.performance(operation, duration, { ...context, success: true });
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.performance(operation, duration, { ...context, success: false });
      this.error(`${operation} failed`, error as Error, context);
      throw error;
    }
  }

  /**
   * Get performance metrics for analytics
   */
  getPerformanceMetrics(): PerformanceMetric[] {
    return [...this.performanceMetrics];
  }

  /**
   * Clear performance metrics
   */
  clearPerformanceMetrics(): void {
    this.performanceMetrics = [];
  }

  /**
   * Get average performance for an operation
   */
  getAveragePerformance(operation: string): number | null {
    const metrics = this.performanceMetrics.filter(m => m.operation === operation);
    if (metrics.length === 0) return null;
    
    const total = metrics.reduce((sum, m) => sum + m.durationMs, 0);
    return total / metrics.length;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<typeof this.config>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Sanitize context to remove sensitive data
   */
  private sanitizeContext(context: LogContext): LogContext {
    const sanitized = { ...context };
    
    // Remove sensitive fields in production
    if (this.isProduction) {
      const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'creditCard', 'ssn'];
      sensitiveKeys.forEach(key => {
        if (key in sanitized) {
          sanitized[key] = '[REDACTED]';
        }
      });
      
      // Redact email addresses in production
      if (sanitized.email && typeof sanitized.email === 'string') {
        sanitized.email = sanitized.email.replace(/(.{2})(.*)(@.*)/, '$1***$3');
      }
    }
    
    return sanitized;
  }

  /**
   * Store performance metric for analytics
   */
  private storePerformanceMetric(metric: PerformanceMetric): void {
    this.performanceMetrics.push(metric);
    
    // Keep only the most recent metrics
    if (this.performanceMetrics.length > this.config.maxMetricsStored) {
      this.performanceMetrics.shift();
    }
  }

  /**
   * Send to error tracking service (Sentry, LogRocket, etc.)
   */
  private sendToErrorTracking(level: LogLevel, message: string, context?: LogContext): void {
    // Only send to Sentry in production
    if (!this.isProduction) return;
    
    try {
      // Use dynamic import to avoid breaking if Sentry is not configured
      if (typeof window !== 'undefined') {
        // Try to access Sentry from window (set by Sentry.init)
        // @ts-ignore - Sentry may not be available
        const Sentry = window.Sentry;
        
        if (Sentry && typeof Sentry.captureMessage === 'function') {
          Sentry.captureMessage(message, {
            level: level === 'error' ? 'error' : level === 'warn' ? 'warning' : 'info',
            extra: this.sanitizeContext(context || {}),
            tags: {
              scope: context?.scope,
              userId: context?.userId,
              petId: context?.petId,
            },
          });
        }
      }
    } catch (error) {
      // Silently fail - don't break logging if Sentry fails
      // This prevents error tracking from causing errors
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience function for creating scoped loggers
export const createLogger = (scope: string, defaultContext?: LogContext) => {
  return logger.createScope(scope, defaultContext);
};

// Export type for external use
export type { LogContext };

// Export for testing
export default logger;

